import { useUpdateExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { startOfDay } from "date-fns";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

type SingleProps = {
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
  expeditions?: never;
};

type BulkProps = {
  expeditions: GetAllExpeditionBoResponseDtoExpedition[];
  expedition?: never;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
} & (SingleProps | BulkProps);

const getInitialDeliveryFromDate = (
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto,
  today: Date,
): Date => {
  const raw = expedition.deliveryInfo?.deliveryFromDate;
  const existing = raw ? new Date(raw) : undefined;
  return existing && existing >= today ? existing : today;
};

export const DeliveryFromDateModal = ({
  open,
  onClose,
  onSuccess,
  expedition,
  expeditions,
}: Props) => {
  const { t } = useTranslation(["shared", "common"]);
  const today = startOfDay(new Date());
  const isBulk = !!expeditions;
  const allExpeditions = isBulk
    ? expeditions
    : expedition
      ? [expedition as GetAllExpeditionBoResponseDtoExpedition]
      : [];

  const [deliveryFromDateValue, setDeliveryFromDateValue] = useState<Date>(() => {
    if (isBulk) return today;
    const existing = expedition ? getInitialDeliveryFromDate(expedition, today) : today;
    return existing;
  });

  const [isPending, setIsPending] = useState(false);
  const isMobile = useIsMobile();

  const { mutateAsync: updateExpeditionBo } = useUpdateExpeditionBo();

  const handleConfirm = async () => {
    if (!deliveryFromDateValue) return;
    setIsPending(true);
    try {
      const noon = new Date(deliveryFromDateValue);
      noon.setHours(12, 0, 0, 0);
      await Promise.all(
        allExpeditions.map((exp) => {
          return updateExpeditionBo({
            id: exp.id,
            payload: { deliveryFromDate: noon.toISOString() },
          });
        }),
      );
      onClose();
      onSuccess?.();
    } finally {
      setIsPending(false);
    }
  };

  const title = t("deliveryFromDateModal.title");
  const description = isBulk
    ? t("deliveryFromDateModal.bulkDescription", { count: allExpeditions.length })
    : t("deliveryFromDateModal.description");

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-fit" aria-describedby="delivery-from-date-description">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription id="delivery-from-date-description">{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col">
          <Calendar
            mode="single"
            selected={deliveryFromDateValue}
            onSelect={(date) => date && setDeliveryFromDateValue(date)}
            defaultMonth={deliveryFromDateValue}
            numberOfMonths={isMobile ? 1 : 2}
            fixedWeeks
            showOutsideDays={false}
            disabled={{ before: today }}
            required
            className="pointer-events-auto self-center rounded-md border"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            className="flex-1"
            disabled={isPending || !deliveryFromDateValue}
          >
            {isPending ? <Loader2 className="animate-spin" /> : t("deliveryFromDateModal.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
